"use client";

import { Image } from "@/components/ui/image";
import { ChevronLeft, ChevronRight, X } from "@/components/icons";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

import { cn } from "@/lib/utils";
import { IconButton } from "@/components/ui/icon-button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";

export interface LightboxPhoto {
  url: string;
  alt?: string;
}

interface PhotoLightboxProps {
  photos: LightboxPhoto[];
  index: number | null;
  onIndexChange: (index: number | null) => void;
  title?: string;
  unoptimized?: boolean;
}

const OVERLAY_CONTROL_CLASS =
  "rounded-full bg-black/60 text-white hover:bg-black/80 hover:text-white focus-visible:ring-white/60";

export function PhotoLightbox({
  photos,
  index,
  onIndexChange,
  title = "Photo preview",
  unoptimized,
}: PhotoLightboxProps) {
  const activeIndex =
    index !== null && index >= 0 && index < photos.length ? index : null;

  const navigate = (direction: -1 | 1) => {
    if (activeIndex === null) return;
    onIndexChange((activeIndex + direction + photos.length) % photos.length);
  };

  return (
    <Dialog
      open={activeIndex !== null}
      onOpenChange={(open) => {
        if (!open) onIndexChange(null);
      }}
    >
      <DialogContent
        hideCloseButton
        overlayClassName="bg-black/90"
        className="max-h-[95dvh] max-w-[min(96vw,1400px)] overflow-hidden border-none bg-transparent p-0 shadow-none"
        onKeyDown={(event) => {
          if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
          event.preventDefault();
          navigate(event.key === "ArrowLeft" ? -1 : 1);
        }}
      >
        <VisuallyHidden>
          <DialogTitle>{title}</DialogTitle>
        </VisuallyHidden>
        {activeIndex === null ? null : (
          <LightboxViewer
            photo={photos[activeIndex]}
            position={activeIndex + 1}
            total={photos.length}
            unoptimized={unoptimized}
            onNavigate={navigate}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface LightboxViewerProps {
  photo: LightboxPhoto;
  position: number;
  total: number;
  unoptimized?: boolean;
  onNavigate: (direction: -1 | 1) => void;
}

function LightboxViewer({
  photo,
  position,
  total,
  unoptimized,
  onNavigate,
}: LightboxViewerProps) {
  return (
    <div className="relative">
      <div className="relative h-[80dvh] w-full">
        <Image
          src={photo.url}
          alt={photo.alt ?? `Photo ${position}`}
          fill
          sizes="(max-width: 1024px) 96vw, 1400px"
          className="object-contain"
          unoptimized={unoptimized}
          loading="eager"
          fetchPriority="high"
        />
      </div>
      <DialogClose asChild>
        <IconButton
          size="default"
          aria-label="Close preview"
          className={cn("absolute right-2 top-2", OVERLAY_CONTROL_CLASS)}
        >
          <X className="h-5 w-5" />
        </IconButton>
      </DialogClose>
      {total > 1 ? (
        <>
          <LightboxStep direction={-1} onNavigate={onNavigate} />
          <LightboxStep direction={1} onNavigate={onNavigate} />
          <p className="mt-2 text-center text-sm text-white/70">
            {position} / {total}
          </p>
        </>
      ) : null}
    </div>
  );
}

function LightboxStep({
  direction,
  onNavigate,
}: {
  direction: -1 | 1;
  onNavigate: (direction: -1 | 1) => void;
}) {
  const isPrevious = direction === -1;
  return (
    <IconButton
      size="default"
      aria-label={isPrevious ? "Previous photo" : "Next photo"}
      onClick={() => onNavigate(direction)}
      className={cn(
        "absolute top-1/2 -translate-y-1/2",
        isPrevious ? "left-2" : "right-2",
        OVERLAY_CONTROL_CLASS,
      )}
    >
      {isPrevious ? (
        <ChevronLeft className="h-5 w-5" />
      ) : (
        <ChevronRight className="h-5 w-5" />
      )}
    </IconButton>
  );
}
